import React, { useState, useRef } from 'react';
import {
    Container,
    Paper,
    TextField,
    Button,
    Box,
    Alert,
    Typography,
    Grid,
    Divider,
    Checkbox,
    FormControlLabel,
    Link
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import trialSignupService from '../../services/trialSignupService';

const DEBOUNCE_MS = 500;

// A check refused for the form's limits (429, S-6.2 pt 5.1) shows its message at the top of the form, as the send's does;
// any other failed check only goes to the console, as before.
const limitedMessage = (error) => (error?.response?.status === 429 && error.response.data?.message) || null;

const TrialSignupForm = ({ onSuccess = null }) => {
    const [formData, setFormData] = useState({
        workspaceName: '',
        domain: '',
        adminEmail: '',
        slug: '',
        first_name: '',
        last_name: '',
        acceptedTerms: false
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [slugAvailable, setSlugAvailable] = useState(null);
    const [domainAvailable, setDomainAvailable] = useState(null);
    // Removed 2 Nov 2026 (S-6.2 pt 5.1): adminAvailable and checkAdmin, which asked the switched-off GET
    // /api/trial/check-admin whether an email is a workspace admin; nothing called it, so its error below never showed.

    const slugDebounceRef = useRef(null);
    const domainDebounceRef = useRef(null);

    // Validate domain and email match
    const validateDomainEmailMatch = (domain, email) => {
        const emailDomain = email.split('@')[1]?.toLowerCase() || '';
        const cleanDomain = domain.replace(/^(www\.)/, '').toLowerCase();
        return emailDomain === cleanDomain;
    };

    // Generate slug from workspace name (debounced, public endpoint)
    const handleWorkspaceNameChange = (e) => {
        const value = e.target.value;
        setFormData((prev) => ({ ...prev, workspaceName: value }));

        if (slugDebounceRef.current) clearTimeout(slugDebounceRef.current);

        if (value.trim()) {
            slugDebounceRef.current = setTimeout(async () => {
                try {
                    const slug = await trialSignupService.generateUniqueSlug(value);
                    setFormData((prev) => ({ ...prev, slug }));
                    checkSlugAvailability(slug);
                } catch (error) {
                    console.error('Error generating slug:', error);
                    if (limitedMessage(error)) setErrorMessage(limitedMessage(error));
                }
            }, DEBOUNCE_MS);
        }
    };

    const checkSlugAvailability = async (slug) => {
        try {
            const available = await trialSignupService.checkSlugAvailability(slug);
            setSlugAvailable(available);
        } catch (error) {
            console.error('Error checking slug availability:', error);
            if (limitedMessage(error)) setErrorMessage(limitedMessage(error));
        }
    };

    const handleSlugChange = (e) => {
        const value = e.target.value;
        setFormData((prev) => ({ ...prev, slug: value }));

        if (slugDebounceRef.current) clearTimeout(slugDebounceRef.current);
        slugDebounceRef.current = setTimeout(() => checkSlugAvailability(value), DEBOUNCE_MS);
    };

    const handleDomainChange = (e) => {
        const value = e.target.value;
        setFormData((prev) => ({ ...prev, domain: value }));

        if (domainDebounceRef.current) clearTimeout(domainDebounceRef.current);
        if (value) {
            domainDebounceRef.current = setTimeout(async () => {
                try {
                    const available = await trialSignupService.checkDomainAvailability(value);
                    setDomainAvailable(available);
                } catch (error) {
                    console.error('Error checking domain availability:', error);
                    if (limitedMessage(error)) setErrorMessage(limitedMessage(error));
                }
            }, DEBOUNCE_MS);
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.workspaceName.trim()) {
            newErrors.workspaceName = 'Workspace name is required';
        }

        if (!formData.domain.trim()) {
            newErrors.domain = 'Domain is required';
        }

        if (!formData.adminEmail.trim()) {
            newErrors.adminEmail = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
            newErrors.adminEmail = 'Invalid email format';
        } else if (!validateDomainEmailMatch(formData.domain, formData.adminEmail)) {
            newErrors.adminEmail = 'Your email domain must match the primary domain you entered. Please use your work email to create this workspace.';
        }

        if (!formData.first_name.trim()) {
            newErrors.first_name = 'First name is required';
        }

        if (!formData.slug.trim()) {
            newErrors.slug = 'Workspace URL is required';
        } else if (slugAvailable === false) {
            newErrors.slug = 'This workspace URL is already taken';
        }

        if (domainAvailable === false) {
            newErrors.domain = 'Another workspace is using this domain. Make sure this is correct for your organization.'; // American English on every screen (K2.3 pt 17)
        }

        if (!formData.acceptedTerms) {
            newErrors.acceptedTerms = 'You must accept the Terms of Service to continue';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // No captcha (S-6.2 pt 5.1, agreed 30 Oct). Removed 2 Nov 2026 (S-6.2 pt 5.1): getCaptchaToken, a stub that sent an
    // empty token, which the server never checked.

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            setErrorMessage('Please fix the errors below');
            return;
        }

        setSubmitting(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const submitData = {
                workspaceName: formData.workspaceName,
                domain: formData.domain,
                adminEmail: formData.adminEmail,
                first_name: formData.first_name,
                last_name: formData.last_name,
                acceptedTerms: formData.acceptedTerms
            };

            await trialSignupService.signup(submitData);

            // A form trial waits for Beetsol's approval, which sends the invitation (K11). Removed 17 Oct 2026 (K11): the
            // message about a verification link, which is no longer sent.
            setSuccessMessage("Thanks. We'll email you when your trial is ready.");

            setFormData({
                workspaceName: '',
                domain: '',
                adminEmail: '',
                slug: '',
                first_name: '',
                last_name: '',
                acceptedTerms: false
            });
            setSlugAvailable(null);
            setDomainAvailable(null);

            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error('Error submitting trial signup:', error);
            setErrorMessage(
                error.response?.data?.message ||
                'An error occurred while creating your account'
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (successMessage) {
        return (
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 2 }} />
                    {/* The one message (K11). Removed 17 Oct 2026 (K11): the 'Check your email' heading. */}
                    <Typography variant="h5">{successMessage}</Typography>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Paper sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>
                    Start your free trial
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                    No credit card required.
                </Typography>

                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                        {errorMessage}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Workspace Name"
                                value={formData.workspaceName}
                                onChange={handleWorkspaceNameChange}
                                error={!!errors.workspaceName}
                                helperText={errors.workspaceName}
                                placeholder="e.g., Acme Corporation"
                                disabled={submitting}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Company Domain"
                                value={formData.domain}
                                onChange={handleDomainChange}
                                error={!!errors.domain || domainAvailable === false}
                                helperText={
                                    errors.domain ||
                                    (domainAvailable === false
                                        ? 'Domain already registered'
                                        : domainAvailable === true
                                        ? 'Domain is available'
                                        : '')
                                }
                                placeholder="e.g., acmecorp.com"
                                disabled={submitting}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="First Name"
                                value={formData.first_name}
                                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                error={!!errors.first_name}
                                helperText={errors.first_name}
                                placeholder="John"
                                disabled={submitting}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Last Name"
                                value={formData.last_name}
                                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                placeholder="Doe"
                                disabled={submitting}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Work Email"
                                type="email"
                                value={formData.adminEmail}
                                onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                                error={!!errors.adminEmail}
                                helperText={
                                    errors.adminEmail || 'Must match your company domain above'
                                }
                                placeholder="admin@acmecorp.com"
                                disabled={submitting}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        Workspace URL
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Workspace URL"
                                value={formData.slug}
                                onChange={handleSlugChange}
                                error={!!errors.slug || slugAvailable === false}
                                helperText={
                                    errors.slug ||
                                    (slugAvailable === false
                                        ? 'This workspace URL is already taken'
                                        : slugAvailable === true
                                        ? 'Available'
                                        : 'Auto-generated from your workspace name — you can edit it')
                                }
                                placeholder="workspace-slug"
                                disabled={submitting}
                            />
                        </Grid>
                    </Grid>

                    {/* Removed 2 Nov 2026 (S-6.2 pt 5.1): the note to mount a captcha widget here (no captcha, agreed 30 Oct). */}

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.acceptedTerms}
                                onChange={(e) =>
                                    setFormData({ ...formData, acceptedTerms: e.target.checked })
                                }
                                disabled={submitting}
                            />
                        }
                        label={
                            <Typography variant="body2">
                                I agree to the{' '}
                                <Link href="https://beetsol.com/terms" target="_blank" rel="noopener">
                                    Terms of Service
                                </Link>{' '}
                                and{' '}
                                <Link href="https://beetsol.com/privacy" target="_blank" rel="noopener">
                                    Privacy Policy
                                </Link>
                            </Typography>
                        }
                    />
                    {errors.acceptedTerms && (
                        <Typography color="error" variant="caption" display="block" sx={{ mb: 2 }}>
                            {errors.acceptedTerms}
                        </Typography>
                    )}

                    <Box sx={{ mt: 3 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            size="large"
                            disabled={submitting}
                        >
                            {submitting ? 'Creating account...' : 'Start Free Trial'}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Container>
    );
};

export default TrialSignupForm;
